/**
 * SettingsCheckout — Iter 191q · Checkout settings.
 *
 * Squarespace-parity checkout configuration. Six sections:
 *   1. Checkout on your domain — status + copyable checkout URL
 *   2. Recover abandoned carts — link to existing cart-recovery automation
 *   3. Minimum order amount — toggle + amount
 *   4. Next order number — starting integer (never decreases)
 *   5. Receipt details — collapsible tax/price/legal receipt copy
 *   6. Email signup — toggle + inline signup-copy customization
 *   7. Default billing address = shipping — toggle
 *   8. Default country in address fields — dropdown
 *   9. Additional fields — collect extra checkout inputs (text/dropdown)
 *  10. Store policies — links to Policies templates page
 *  11. Checkout page customization — links to Brand → checkout theme
 *
 * All state persists under `settings.checkout` via GET/PUT /api/builder/settings.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search, Loader2, Save, ChevronDown, ChevronRight, Copy, Check,
  ShoppingCart, RefreshCw, DollarSign, Hash, ReceiptText, Mail,
  MapPin, Globe, Plus, Trash2, Scroll, Palette, ExternalLink,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import BackToSettings from './_BackToSettings';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const DEFAULTS = {
  domain_checkout_enabled: true,
  minimum_order_enabled: false,
  minimum_order_amount: 0,
  minimum_order_currency: 'USD',
  next_order_number: 1,
  receipt_details: {
    show_tax_summary: true,
    business_number: '',
    footer_note: '',
    price_display: 'tax_exclusive',   // 'tax_inclusive' | 'tax_exclusive'
  },
  email_signup: {
    enabled: true,
    checkbox_label: 'Sign up for offers, news, and updates',
    checked_by_default: false,
    consent_text: '',
  },
  default_billing_is_shipping: false,
  default_country: 'US',
  additional_fields: [],   // [{ id, label, type: 'text'|'select', required, options: [] }]
  store_policies: {
    returns: '', privacy: '', tos: '',
  },
};

const COUNTRIES = ['US', 'CA', 'MX', 'GB', 'AU', 'NZ', 'FR', 'DE', 'ES', 'IT', 'JP', 'BR'];

/* ─────────── Primitives ─────────── */
function Toggle({ checked, onChange, disabled, testid }) {
  return (
    <button
      type="button" role="switch" aria-checked={checked}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled} data-testid={testid}
      className={`relative w-10 h-6 rounded-full transition-colors ${checked ? 'bg-emerald-400' : 'bg-white/15'} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span className={`absolute top-[2px] left-[2px] w-5 h-5 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : 'translate-x-0'}`} />
    </button>
  );
}

function Card({ children, testid }) {
  return (
    <section className="rounded-xl bg-white/[0.03] border border-white/[0.08] p-6" data-testid={testid}>
      {children}
    </section>
  );
}

function CardHead({ icon: Icon, title, description, right }) {
  return (
    <div className="flex items-start justify-between gap-6">
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-bold text-white flex items-center gap-2">
          {Icon && <Icon size={14} className="text-[#A78BFA]" />} {title}
        </div>
        {description && <p className="mt-1 text-[12.5px] text-[#94A3B8] leading-snug">{description}</p>}
      </div>
      {right && <div className="shrink-0">{right}</div>}
    </div>
  );
}

/* ─────────── Page ─────────── */
export default function SettingsCheckout() {
  const [cfg, setCfg] = useState(DEFAULTS);
  const [siteMeta, setSiteMeta] = useState({ subdomain: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [emailSettingsOpen, setEmailSettingsOpen] = useState(false);
  const [additionalOpen, setAdditionalOpen] = useState(false);
  const [policiesOpen, setPoliciesOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/builder/settings`, { headers: authHeaders() });
        const remote = data?.checkout;
        if (remote) {
          setCfg({
            ...DEFAULTS,
            ...remote,
            receipt_details: { ...DEFAULTS.receipt_details, ...(remote.receipt_details || {}) },
            email_signup:    { ...DEFAULTS.email_signup, ...(remote.email_signup || {}) },
            store_policies:  { ...DEFAULTS.store_policies, ...(remote.store_policies || {}) },
            additional_fields: Array.isArray(remote.additional_fields) ? remote.additional_fields : [],
          });
        }
        setSiteMeta({ subdomain: data?.subdomain || '' });
      } catch { /* first-time */ }
      finally { setLoading(false); }
    })();
  }, []);

  const update = (patch) => { setCfg((c) => ({ ...c, ...patch })); setDirty(true); };
  const updateNested = (key, patch) => { setCfg((c) => ({ ...c, [key]: { ...c[key], ...patch } })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/builder/settings`, { checkout: cfg }, { headers: authHeaders() });
      toast.success('Checkout settings saved');
      setDirty(false);
    } catch { toast.error('Could not save'); }
    finally { setSaving(false); }
  };

  const checkoutUrl = siteMeta.subdomain
    ? `https://${siteMeta.subdomain}.entreprenatives.com/checkout`
    : 'https://yoursite.entreprenatives.com/checkout';
  const doCopy = () => {
    navigator.clipboard?.writeText(checkoutUrl);
    setCopied(true); setTimeout(() => setCopied(false), 1200);
  };

  const addField = () => update({
    additional_fields: [...cfg.additional_fields, { id: `f_${Date.now()}`, label: 'New field', type: 'text', required: false, options: [] }],
  });
  const patchField = (idx, patch) => update({
    additional_fields: cfg.additional_fields.map((f, i) => i === idx ? { ...f, ...patch } : f),
  });
  const removeField = (idx) => update({
    additional_fields: cfg.additional_fields.filter((_, i) => i !== idx),
  });

  return (
    <div className="min-h-full" data-testid="settings-checkout-page">
      {/* Top rail */}
      <div className="border-b border-white/[0.06] bg-white/[0.02] backdrop-blur-xl px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BackToSettings />
          <div className="w-px h-4 bg-white/10" />
          <h1 className="text-[13px] font-bold text-white uppercase tracking-[0.18em]">Settings</h1>
        </div>
        <div className="flex items-center gap-3">
          <button className="w-8 h-8 grid place-items-center text-white/60 hover:text-white hover:bg-white/[0.06] rounded-md" title="Search settings">
            <Search size={14} />
          </button>
          {saving && <span className="text-[10px] uppercase tracking-[0.14em] text-[#A78BFA] font-bold animate-pulse">Saving…</span>}
          <button
            type="button" onClick={save} disabled={!dirty || saving}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[11px] font-bold uppercase tracking-[0.14em] ${dirty && !saving ? 'bg-[#A78BFA] text-[#0E0E23] hover:bg-white' : 'bg-white/5 text-white/40 cursor-not-allowed'}`}
            data-testid="checkout-save-btn"
          >
            <Save size={12} /> Save
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-8 py-10">
        <div className="text-[12px] text-[#64748B] mb-3">
          <span className="text-white/70 font-semibold">Selling</span>
          <span className="mx-2">/</span>
          <span className="text-white">Checkout</span>
        </div>
        <h2 className="text-[32px] font-bold text-white tracking-tight" style={{ fontFamily: 'Syne, Inter, sans-serif' }}>Checkout</h2>

        {loading ? (
          <div className="mt-10 flex items-center gap-2 text-white/50"><Loader2 size={14} className="animate-spin" /> Loading…</div>
        ) : (
          <div className="mt-8 space-y-4">
            {/* 1. Checkout on your domain */}
            <Card testid="section-domain-checkout">
              <CardHead
                icon={Globe}
                title="Checkout on your domain"
                description="Customers are directed to a secure checkout on your own domain instead of a third-party page. Boosts trust and conversion."
                right={
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-400/25">
                    <Check size={10} /> Enabled
                  </span>
                }
              />
              <div className="mt-4">
                <div className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-[#64748B] mb-2">Your Checkout URL</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-[#0A0A1F] border border-white/10 rounded-md px-3 py-2 text-[12.5px] text-white/85 font-mono break-all">{checkoutUrl}</code>
                  <button onClick={doCopy} className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-white/5 border border-white/10 text-white text-[11px] font-semibold hover:bg-white/10" data-testid="copy-checkout-url">
                    {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>
            </Card>

            {/* 2. Recover abandoned carts */}
            <Card testid="section-cart-recovery">
              <CardHead icon={RefreshCw} title="Recover abandoned carts" description="Automatically send customers a reminder email to complete their purchase 1 day after they abandon their cart." />
              <div className="mt-4">
                <Link
                  to="/website-builder/settings/cart-recovery"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-black text-white text-[11px] font-bold uppercase tracking-wider hover:bg-[#111]"
                  data-testid="cart-recovery-btn"
                >
                  Set up automation <ChevronRight size={12} />
                </Link>
              </div>
            </Card>

            {/* 3. Minimum order amount */}
            <Card testid="section-min-order">
              <CardHead
                icon={DollarSign}
                title="Minimum order amount"
                description="Require customers to reach a minimum subtotal before they can check out. Available for one-time physical, service, download, and donation products."
                right={<Toggle checked={cfg.minimum_order_enabled} onChange={(v) => update({ minimum_order_enabled: v })} testid="min-order-toggle" />}
              />
              {cfg.minimum_order_enabled && (
                <div className="mt-4 flex items-center gap-2 max-w-xs">
                  <span className="text-white/40 text-sm">$</span>
                  <input
                    type="number" min="0" step="0.01"
                    value={cfg.minimum_order_amount}
                    onChange={(e) => update({ minimum_order_amount: Number(e.target.value) || 0 })}
                    className="flex-1 bg-[#0A0A1F] border border-white/10 rounded-md px-3 py-2 text-[13px] text-white focus:outline-none focus:border-[#A78BFA]/60"
                    data-testid="min-order-amount"
                  />
                  <select
                    value={cfg.minimum_order_currency}
                    onChange={(e) => update({ minimum_order_currency: e.target.value })}
                    className="bg-[#0A0A1F] border border-white/10 rounded-md px-2 py-2 text-[13px] text-white focus:outline-none focus:border-[#A78BFA]/60"
                    data-testid="min-order-currency"
                  >
                    {['USD', 'CAD', 'GBP', 'EUR', 'AUD'].map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              )}
            </Card>

            {/* 4. Next order number */}
            <Card testid="section-next-order-number">
              <CardHead icon={Hash} title="Next order number" description="Every order is assigned a number. Assign a number for your next order — cannot decrease below the highest existing order." />
              <input
                type="number" min="1"
                value={cfg.next_order_number}
                onChange={(e) => update({ next_order_number: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                className="mt-4 w-full max-w-xs bg-[#0A0A1F] border border-white/10 rounded-md px-3 py-2 text-[13px] text-white focus:outline-none focus:border-[#A78BFA]/60"
                data-testid="next-order-number"
              />
            </Card>

            {/* 5. Receipt details (collapsible) */}
            <Card testid="section-receipt-details">
              <button className="w-full text-left" onClick={() => setReceiptOpen(o => !o)} data-testid="receipt-toggle">
                <CardHead
                  icon={ReceiptText}
                  title="Receipt details"
                  description="Customize price and tax details on your receipts based on local requirements."
                  right={<ChevronRight size={16} className={`text-white/40 transition-transform ${receiptOpen ? 'rotate-90' : ''}`} />}
                />
              </button>
              {receiptOpen && (
                <div className="mt-5 pt-5 border-t border-white/[0.05] space-y-4">
                  <div>
                    <div className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-[#64748B] mb-1.5">Price display</div>
                    <div className="inline-flex rounded-md border border-white/10 overflow-hidden">
                      {[{ id: 'tax_exclusive', label: 'Tax exclusive' }, { id: 'tax_inclusive', label: 'Tax inclusive' }].map((o) => (
                        <button key={o.id} type="button" onClick={() => updateNested('receipt_details', { price_display: o.id })} className={`px-4 py-2 text-[11px] font-semibold uppercase tracking-wider ${cfg.receipt_details.price_display === o.id ? 'bg-[#A78BFA] text-[#0E0E23]' : 'bg-transparent text-white/60 hover:bg-white/5'}`} data-testid={`receipt-price-${o.id}`}>{o.label}</button>
                      ))}
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-[13px] text-white/80">
                    <input type="checkbox" checked={cfg.receipt_details.show_tax_summary} onChange={(e) => updateNested('receipt_details', { show_tax_summary: e.target.checked })} data-testid="receipt-tax-summary" />
                    Show tax breakdown on receipts
                  </label>
                  <label className="block">
                    <span className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-[#64748B]">Business tax number (GST / VAT / EIN)</span>
                    <input value={cfg.receipt_details.business_number} onChange={(e) => updateNested('receipt_details', { business_number: e.target.value })} placeholder="e.g. VAT GB123456789" className="mt-1.5 w-full bg-[#0A0A1F] border border-white/10 rounded-md px-3 py-2 text-[13px] text-white focus:outline-none focus:border-[#A78BFA]/60" data-testid="receipt-business-number" />
                  </label>
                  <label className="block">
                    <span className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-[#64748B]">Receipt footer note</span>
                    <textarea rows={2} value={cfg.receipt_details.footer_note} onChange={(e) => updateNested('receipt_details', { footer_note: e.target.value })} placeholder="Thank you for supporting an Indigenous-owned business." className="mt-1.5 w-full bg-[#0A0A1F] border border-white/10 rounded-md px-3 py-2 text-[13px] text-white focus:outline-none focus:border-[#A78BFA]/60 resize-y" data-testid="receipt-footer" />
                  </label>
                </div>
              )}
            </Card>

            {/* Section: Checkout page */}
            <h3 className="pt-6 text-[16px] font-bold text-white tracking-tight" style={{ fontFamily: 'Syne, Inter, sans-serif' }}>Checkout page</h3>

            {/* 6. Email signup */}
            <Card testid="section-email-signup">
              <CardHead
                icon={Mail}
                title="Email signup"
                description="Allow customers to sign up for your marketing emails during checkout. Subscribers are stored in Contacts."
                right={<Toggle checked={cfg.email_signup.enabled} onChange={(v) => updateNested('email_signup', { enabled: v })} testid="email-signup-toggle" />}
              />
              <button className="mt-4 w-full text-left flex items-center justify-between p-3 rounded-lg bg-white/[0.02] border border-white/[0.06] hover:bg-white/[0.04]" onClick={() => setEmailSettingsOpen(o => !o)} data-testid="email-signup-settings-btn">
                <div>
                  <div className="text-[13px] font-semibold text-white">Email signup settings</div>
                  <div className="text-[11.5px] text-[#94A3B8] mt-0.5">Customize the signup checkbox and add additional storage options.</div>
                </div>
                <ChevronRight size={14} className={`text-white/40 transition-transform ${emailSettingsOpen ? 'rotate-90' : ''}`} />
              </button>
              {emailSettingsOpen && (
                <div className="mt-4 space-y-3 pl-3 border-l-2 border-[#A78BFA]/25">
                  <label className="block">
                    <span className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-[#64748B]">Checkbox label</span>
                    <input value={cfg.email_signup.checkbox_label} onChange={(e) => updateNested('email_signup', { checkbox_label: e.target.value })} className="mt-1.5 w-full bg-[#0A0A1F] border border-white/10 rounded-md px-3 py-2 text-[13px] text-white focus:outline-none focus:border-[#A78BFA]/60" data-testid="email-signup-label" />
                  </label>
                  <label className="flex items-center gap-2 text-[13px] text-white/80">
                    <input type="checkbox" checked={cfg.email_signup.checked_by_default} onChange={(e) => updateNested('email_signup', { checked_by_default: e.target.checked })} data-testid="email-signup-default" />
                    Pre-check the box (opt-out) — comply with your region&apos;s consent laws
                  </label>
                  <label className="block">
                    <span className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-[#64748B]">Consent legalese (optional)</span>
                    <textarea rows={2} value={cfg.email_signup.consent_text} onChange={(e) => updateNested('email_signup', { consent_text: e.target.value })} placeholder="By subscribing you agree to receive marketing emails from us. Unsubscribe any time." className="mt-1.5 w-full bg-[#0A0A1F] border border-white/10 rounded-md px-3 py-2 text-[13px] text-white focus:outline-none focus:border-[#A78BFA]/60 resize-y" data-testid="email-signup-consent" />
                  </label>
                </div>
              )}
            </Card>

            {/* 7. Default billing = shipping */}
            <Card testid="section-billing-default">
              <CardHead
                icon={MapPin}
                title="Default billing address"
                description='"Use shipping address" will be selected by default for the billing address. Customers can always enter a different address.'
                right={<Toggle checked={cfg.default_billing_is_shipping} onChange={(v) => update({ default_billing_is_shipping: v })} testid="billing-default-toggle" />}
              />
            </Card>

            {/* 8. Default country */}
            <Card testid="section-default-country">
              <CardHead icon={Globe} title="Default country in address fields" description="Pre-select a country for shipping and billing addresses." />
              <select
                value={cfg.default_country}
                onChange={(e) => update({ default_country: e.target.value })}
                className="mt-4 w-full max-w-xs bg-[#0A0A1F] border border-white/10 rounded-md px-3 py-2 text-[13px] text-white focus:outline-none focus:border-[#A78BFA]/60"
                data-testid="default-country"
              >
                {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Card>

            {/* 9. Additional fields */}
            <Card testid="section-additional-fields">
              <button className="w-full text-left" onClick={() => setAdditionalOpen(o => !o)} data-testid="additional-toggle">
                <CardHead icon={Plus} title="Additional fields" description={`Collect additional information from customers during checkout. ${cfg.additional_fields.length ? `(${cfg.additional_fields.length} configured)` : ''}`}
                  right={<ChevronRight size={16} className={`text-white/40 transition-transform ${additionalOpen ? 'rotate-90' : ''}`} />}
                />
              </button>
              {additionalOpen && (
                <div className="mt-5 pt-5 border-t border-white/[0.05]">
                  <button onClick={addField} className="mb-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wider bg-[#A78BFA] text-[#0E0E23] hover:bg-white" data-testid="add-field-btn"><Plus size={11} /> Add field</button>
                  {cfg.additional_fields.length === 0 && <p className="text-[11.5px] italic text-white/40">No custom fields yet. Common examples: gift message, delivery instructions, preferred contact method.</p>}
                  <div className="space-y-2">
                    {cfg.additional_fields.map((f, idx) => (
                      <div key={f.id || idx} className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3 grid grid-cols-[1fr_120px_100px_auto] gap-2 items-center" data-testid={`field-row-${idx}`}>
                        <input value={f.label} onChange={(e) => patchField(idx, { label: e.target.value })} placeholder="Label" className="px-2 py-1.5 rounded bg-[#0A0A1F] border border-white/10 text-white text-xs outline-none focus:border-[#A78BFA]/50" data-testid={`field-label-${idx}`} />
                        <select value={f.type} onChange={(e) => patchField(idx, { type: e.target.value })} className="px-2 py-1.5 rounded bg-[#0A0A1F] border border-white/10 text-white text-xs outline-none focus:border-[#A78BFA]/50" data-testid={`field-type-${idx}`}>
                          <option value="text">Text</option>
                          <option value="textarea">Long text</option>
                          <option value="select">Dropdown</option>
                          <option value="checkbox">Checkbox</option>
                        </select>
                        <label className="flex items-center gap-1 text-[11px] text-white/70">
                          <input type="checkbox" checked={!!f.required} onChange={(e) => patchField(idx, { required: e.target.checked })} data-testid={`field-required-${idx}`} /> Required
                        </label>
                        <button onClick={() => removeField(idx)} className="w-7 h-7 rounded bg-red-500/10 hover:bg-red-500/20 flex items-center justify-center"><Trash2 size={11} className="text-red-400" /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            {/* 10. Store policies */}
            <Card testid="section-store-policies">
              <button className="w-full text-left" onClick={() => setPoliciesOpen(o => !o)} data-testid="policies-toggle">
                <CardHead icon={Scroll} title="Store policies" description="Define your return policy, terms of service, and privacy policy — shown to buyers at checkout."
                  right={<ChevronRight size={16} className={`text-white/40 transition-transform ${policiesOpen ? 'rotate-90' : ''}`} />}
                />
              </button>
              {policiesOpen && (
                <div className="mt-5 pt-5 border-t border-white/[0.05] space-y-4">
                  {[
                    { key: 'returns', label: 'Return / refund policy', placeholder: 'Explain your return window and process.' },
                    { key: 'tos',     label: 'Terms of service',       placeholder: 'The rules customers agree to at purchase.' },
                    { key: 'privacy', label: 'Privacy policy',         placeholder: 'How you collect, use, and store customer data.' },
                  ].map(({ key, label, placeholder }) => (
                    <label key={key} className="block">
                      <span className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-[#64748B]">{label}</span>
                      <textarea rows={3} value={cfg.store_policies[key]} onChange={(e) => updateNested('store_policies', { [key]: e.target.value })} placeholder={placeholder} className="mt-1.5 w-full bg-[#0A0A1F] border border-white/10 rounded-md px-3 py-2 text-[13px] text-white focus:outline-none focus:border-[#A78BFA]/60 resize-y" data-testid={`policy-${key}`} />
                    </label>
                  ))}
                </div>
              )}
            </Card>

            {/* Section: Checkout style */}
            <h3 className="pt-6 text-[16px] font-bold text-white tracking-tight" style={{ fontFamily: 'Syne, Inter, sans-serif' }}>Checkout style</h3>

            <Card testid="section-checkout-customization">
              <div className="flex items-start justify-between gap-6">
                <div>
                  <div className="text-[14px] font-bold text-white flex items-center gap-2">
                    <Palette size={14} className="text-[#A78BFA]" /> Checkout page customization
                  </div>
                  <p className="mt-1 text-[12.5px] text-[#94A3B8] leading-snug">Upload a logo, change colors, and personalize the checkout page look.</p>
                </div>
                <Link
                  to="/website-builder/settings/favicon"
                  className="shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded-md bg-white/5 border border-white/10 text-white text-[11px] font-semibold hover:bg-white/10"
                  data-testid="customize-checkout-link"
                >
                  Customize <ExternalLink size={11} />
                </Link>
              </div>
            </Card>

            <div className="h-12" />
          </div>
        )}
      </div>
    </div>
  );
}
